/* eslint-disable @typescript-eslint/no-unused-vars */
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider, useSelector } from 'react-redux';
import relativeTime from 'dayjs/plugin/relativeTime';
import './App.css';
import dayjs from 'dayjs';
import theme from './utils/light.theme';
import store, { persistor } from './store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './i18n/config';
import Toast from './common/@the-source/Toast';
import Loading from './screens/Loading/Loading';
import { LicenseManager, SetFilter } from 'ag-grid-enterprise';
import { useDispatch } from 'react-redux';
import { fetch_user_details } from './actions/login';
import Tracker from '@openreplay/tracker';
import { createFreshAuthRouter, MainRouter } from './utils/RouteNames';
import { set_permissions } from './actions/permissions';

const { VITE_APP_ENV, VITE_APP_TRACKER_KEY } = import.meta.env;
const AG_GRID_LICENSE_KEY = import.meta.env.VITE_AG_GRID_LICENSE_KEY ? import.meta.env.VITE_AG_GRID_LICENSE_KEY : '';
LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);

// AG Grid 31 destroys set filters when filterParams (e.g. an inline keyCreator) change on a columnDefs update; v30 kept them.
// Restores v30 behaviour, same as ultron #6544. Verified against ag-grid-enterprise 31.3.4; re-check on upgrade.
SetFilter.prototype.refresh = () => true;
dayjs.extend(relativeTime);

const PrimaryComp = () => {
	const login = useSelector((state: any) => state.login);
	const [is_loading, set_is_loading] = useState(true);
	const dispatch = useDispatch();
	const access_token = useSelector((state: any) => state.persistedUserData.auth_access_token);

	useEffect(() => {
		set_is_loading(false);
	}, []);

	useEffect(() => {
		const user = login?.userDetails;

		if (!user?.id || !user?.email || !user?.tenant_id) return;
		if (!window?.pendo?.initialize) return;

		const visitor = {
			id: user?.id,
			name: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
			email: user?.email,
		};
		const account = {
			id: user?.tenant_id,
		};
		window.pendo.initialize({ visitor, account });
	}, [login?.userDetails]);

	const authRouterSeq = useRef(0);
	const authRouter = useMemo(() => {
		authRouterSeq.current += 1;
		return createFreshAuthRouter();
	}, [login.status.loggedIn]);

	useEffect(() => {
		const user_details = login?.userDetails;
		if (VITE_APP_ENV === 'production' && login.status.loggedIn && user_details?.id) {
			const tracker = new Tracker({
				projectKey: VITE_APP_TRACKER_KEY,
				__DISABLE_SECURE_MODE: true,
				captureExceptions: true,
				captureIFrames: true,
				network: {
					sessionTokenHeader: false,
					capturePayload: true,
					failuresOnly: false,
					ignoreHeaders: false,
					captureInIframes: true,
				},
			});

			tracker
				.start({
					userID: user_details?.email,
					metadata: {
						email: user_details?.email,
						mobile: `${user_details?.country_code} ${user_details?.phone}`,
						isAdmin: user_details?.is_admin,
						userId: user_details?.id,
					},
				})
				.then(() => {
					console.log('Tracker Started');
				})
				.catch((error) => {
					console.error('Tracker failed to start:', error);
				});
		}
	}, [login?.userDetails?.id]);

	const app_router = () => {
		if (!login?.status?.loggedIn) return <RouterProvider key={authRouterSeq.current} router={authRouter} />;
		if (is_loading) return <Loading />;
		return <RouterProvider router={MainRouter} />;
	};

	useEffect(() => {
		if (access_token) {
			dispatch<any>(fetch_user_details());
		}
	}, [dispatch, access_token]);

	useEffect(() => {
		if (!access_token) return;
		dispatch<any>(set_permissions());
	}, [dispatch, access_token, login?.refetch_permissions]);

	return (
		<React.Fragment>
			{/* {constants.ALL_ENV.PRODUCTION !== VITE_APP_ENV && (
				<Grid style={{ backgroundColor: '#FF8282' }}>
					<CustomText type='Caption' style={{ textAlign: 'center', textTransform: 'capitalize' }}>
						{VITE_APP_ENV}
					</CustomText>
				</Grid>
			)} */}
			{app_router()}
			<Toast />
		</React.Fragment>
	);
};

const App = () => {
	return (
		<MuiThemeProvider theme={theme}>
			<Provider store={store}>
				<PersistGate loading={null} persistor={persistor}>
					<PrimaryComp />
				</PersistGate>
			</Provider>
		</MuiThemeProvider>
	);
};

export default App;
